"use client";

import React from "react";
import { ArrowRight, BookOpen, Calculator, Compass, FileText, CircleQuestionMark as HelpCircle, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HomeAdditionCallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section ref={ref} id="homeadditioncalltoaction" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Text and Primary Action */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15]">
              Expand your Brookhaven home with seamless architectural additions.
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
              Whether you are planning a ground-floor primary suite, a light-filled sunroom addition, or an expanded second story, Brookhaven Elevation Renovations manages zoning, architecture, and structural craftsmanship from initial concept to turnkey completion.
            </p>

            <div className="pt-2">
              <Button asChild size="lg" className="h-12 px-7 text-base font-medium shadow-sm hover:shadow-md transition-all">
                <Link to="/ContactRequestAnEstimatePage" onClick={e => {
                const el = document.getElementById("estimaterequestform");
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                  });
                }
              }}>
                  Request an Estimate
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Realistic Home Addition Imagery */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            <div className="relative overflow-hidden rounded-xl border border-border shadow-sm group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-sizes" alt="Architectural two-story home addition with harmonious brickwork and custom windows in Brookhaven" loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="relative overflow-hidden rounded-xl border border-border shadow-sm group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_interior.png-wvc-sizes" alt="Interior view of a spacious sunroom and vaulted family room addition with floor-to-ceiling glass" loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
          </div>
        </div>

        <Separator className="my-14" />

        {/* Secondary Navigation & Related Services */}
        <div className="space-y-8">
          {/* Related Services Row */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-2 border-b border-border/60 pb-6">
            <span className="text-sm font-semibold tracking-wide uppercase text-foreground">
              Related Services
            </span>
            <div className="flex flex-wrap items-center gap-4 sm:gap-8">
              <Link to="#" className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                <Layers className="size-4 text-primary" />
                Basement Remodeling
              </Link>
              <Link to="#" className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                <Compass className="size-4 text-primary" />
                Whole-Home Remodeling
              </Link>
            </div>
          </div>

          {/* Planning Resources & FAQ Links */}
          <div className="flex flex-wrap items-center justify-between gap-6 pt-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
              Planning Resources
            </span>
            <div className="flex flex-wrap items-center gap-6 sm:gap-8">
              <Link to="#" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Calculator className="size-4" />
                Remodeling Cost Guide
              </Link>
              <Link to="#" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <FileText className="size-4" />
                Remodeling Planning Guide
              </Link>
              <Link to="#" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <HelpCircle className="size-4" />
                Frequently Asked Questions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}